import React from 'react';
import { Marker } from 'react-leaflet';
import { RoutePoint, RoutePointType } from '../RouteConfirmation';
import { 
  AmberDropPointIcon, 
  CurrentDropPointIcon, 
  CurrentDropPointIconRed, 
  DroneMarker, 
  SelectedPointMarker, 
  WaypointMarker, 
  WaypointMarkerArrow 
} from './MapIcons';

interface RouteArrowMarkersProps {
  routeCoords: RoutePoint[];
  onRemoveMarker: (e: any, routeCoordIndex: number) => void;
  onUpdateMarker: (e: any, routeCoordIndex: number) => void;
  onBumpUpdated: () => void;
  canDragMarkers?: boolean;
  canRemoveMarkers?: boolean;
  lockMirrorMarkers?: boolean;
  dropIndex?: number;
}

const getMarkerIcon = (
  isFirstCoord: boolean,
  isSelected: boolean,
  isDropPoint: boolean,
  isLastCoord: boolean,
  proposedNewDropPoint: any,
  rotationAngle: number,
  markerArrowsEnabled: boolean
) => {
  if (isDropPoint || isLastCoord) {
    if (!proposedNewDropPoint) {
      return CurrentDropPointIcon;
    }

    if (proposedNewDropPoint?.isLowRisk) {
      return CurrentDropPointIcon;
    } else if (proposedNewDropPoint?.isEvaluated && !proposedNewDropPoint?.isLowRisk) {
      return CurrentDropPointIconRed;
    } else {
      return AmberDropPointIcon;
    }
  }

  if (isSelected) {
    return SelectedPointMarker;
  } else if (isFirstCoord || isLastCoord) {
    return DroneMarker;
  }

  return markerArrowsEnabled ? WaypointMarkerArrow(rotationAngle) : WaypointMarker;
};

const RouteArrowMarkersComponent = ({
  routeCoords,
  onRemoveMarker,
  onUpdateMarker,
  onBumpUpdated,
  canDragMarkers = true,
  canRemoveMarkers = true,
  lockMirrorMarkers = false,
  dropIndex
}: RouteArrowMarkersProps) => {
  if (!routeCoords) {
    return null;
  }
  return (
    <>
      {routeCoords.map(routeCoord => {
        const routeCoordIndex = Number(routeCoord.index);
        const isLastCoord = routeCoords.length - 1 === routeCoordIndex;
        const isDropPoint = routeCoord.type === RoutePointType.CUSTOMER_DROP_LOCATION;
        const isFirstCoord = routeCoordIndex === 0;
        const isInboundMirror = typeof dropIndex === 'number' && routeCoordIndex > (dropIndex as number);

        let angle = 0;
        if (!isFirstCoord && !isDropPoint && !isLastCoord) {
          const to = routeCoords[routeCoordIndex + 1];
          const from = routeCoord;
          const dy = to.coordinates[0] - from.coordinates[0];
          const dx = Math.cos(Math.PI / 180 * from.coordinates[0]) * (to.coordinates[1] - from.coordinates[1]);
          angle = ((Math.atan2(dy, dx) / Math.PI) * 180 * -1);
        }

        // Allow dragging for internal waypoints regardless of type label
        // (mission data may provide 'point' instead of 'waypoint').
        const shouldDisableDrag = isFirstCoord || isDropPoint || isLastCoord || (lockMirrorMarkers && isInboundMirror);
        const isDraggable = canDragMarkers && !shouldDisableDrag;
        const clickHandler = canRemoveMarkers && routeCoord.type === RoutePointType.WAYPOINT && !(lockMirrorMarkers && isInboundMirror)
          ? (e: any) => {
              onRemoveMarker(e, routeCoordIndex);
            }
          : undefined;

        return (
          <Marker
            key={`routecoords-${routeCoord.index}`}
            opacity={1}
            // @ts-ignore
            marker_index={routeCoordIndex}
            icon={getMarkerIcon(isFirstCoord, false, isDropPoint, isLastCoord, false, angle, true)}
            position={routeCoord.coordinates}
            zIndexOffset={isInboundMirror ? 0 : 1000}
            eventHandlers={{
              ...(clickHandler ? { click: clickHandler } : {}),
              dragend: async e => {
                if (!isDraggable) {
                  return;
                }
                onUpdateMarker(e, routeCoordIndex);
                onBumpUpdated();
              }
            }}
            draggable={isDraggable}
          />
        );
      })}
    </>
  );
};

const areRouteCoordsEqual = (prev: RoutePoint[] | undefined, next: RoutePoint[] | undefined): boolean => {
  if (prev === next) { return true; }
  if (!prev && !next) { return true; }
  if (!prev || !next) { return false; }
  if (prev.length !== next.length) { return false; }

  for (let i = 0; i < prev.length; i++) {
    const a = prev[i];
    const b = next[i];
    if (a.index !== b.index) { return false; }
    if (!a.coordinates || !b.coordinates) { return false; }
    if (a.coordinates[0] !== b.coordinates[0]) { return false; }
    if (a.coordinates[1] !== b.coordinates[1]) { return false; }
  }
  return true;
};

export const RouteArrowMarkers = React.memo(RouteArrowMarkersComponent, (prevProps, nextProps) => {
  return areRouteCoordsEqual(prevProps.routeCoords, nextProps.routeCoords);
});


